import styled from '@emotion/styled'
import React from 'react'

import { Chip, Tooltip } from '@mui/material'
import { blue } from '@mui/material/colors'
import { Stack, Theme, useTheme } from '@mui/system'
import { isSpecialUnitIssue } from '../../pages/issues/utils'
import { IssueEntity, IssueType } from '../../types/IssueEntity'
import { WarrantEntity } from 'types/WarrantEntity'

export const LabelChip = styled(Chip)`
    height: 20px;
    padding: 4px 0;
    font-size: 85%;

    span {
        padding-left: ${(props) => props.theme.spacing(2)};
        padding-right: ${(props) => props.theme.spacing(2)};
    }
`

export const FELabel = ({ onClick, noTooltip = false }: { onClick?: React.MouseEventHandler; noTooltip?: boolean }) => {
    const theme = useTheme()

    if (noTooltip) {
        return <LabelChip label='FE' onClick={onClick} sx={getIssueTypeColors(theme, 'Rights issue')} />
    }

    return (
        <Tooltip title='Företrädesemission'>
            <LabelChip label='FE' onClick={onClick} sx={getIssueTypeColors(theme, 'Rights issue')} />
        </Tooltip>
    )
}

export const SPECLabel = ({ noTooltip = false }: { noTooltip?: boolean }) => {
    const theme = useTheme()
    if (noTooltip) {
        return (
            <LabelChip
                label='SPEC'
                sx={{
                    backgroundColor: theme.palette.SPEC.main,
                    color: theme.palette.common.black,
                }}
            />
        )
    }
    return (
        <Tooltip title='Speciell unitemission'>
            <LabelChip
                label='SPEC'
                sx={{
                    backgroundColor: theme.palette.SPEC.main,
                    color: theme.palette.common.black,
                }}
            />
        </Tooltip>
    )
}

export const HasWarrantLabel = ({
    isClickable,
    warrant,
    noTooltip = false,
}: {
    isClickable?: boolean
    warrant?: Pick<WarrantEntity, 'id'>
    noTooltip?: boolean
}) => {
    const theme = useTheme()
    const handleClick = () => {
        const path = `/warrants/${(warrant as WarrantEntity).id}`

        window.open(path, '_blank')
    }

    if (noTooltip) {
        return (
            <LabelChip
                label='TO'
                onClick={isClickable && warrant ? handleClick : undefined}
                sx={{
                    backgroundColor: theme.palette.TO.main,
                    color: theme.palette.common.black,
                }}
            />
        )
    }

    return (
        <Tooltip title='Teckningsoption'>
            <LabelChip
                label='TO'
                onClick={isClickable && warrant ? handleClick : undefined}
                sx={{
                    backgroundColor: theme.palette.TO.main,
                    color: theme.palette.common.black,
                }}
            />
        </Tooltip>
    )
}

// const LinkedIssueLabel = () => (
//     <Tooltip title='Länkad emission'>
//         <LabelChip label={<LinkIcon />} bg={indigo} />
//     </Tooltip>
// )
export const NonCashIssueLabel = ({
    onClick,
    noTooltip = false,
}: {
    onClick?: React.MouseEventHandler
    noTooltip?: boolean
}) => {
    const theme = useTheme()

    if (noTooltip) {
        return <LabelChip label='AE' onClick={onClick} sx={getIssueTypeColors(theme, 'Non-cash issue')} />
    }

    return (
        <Tooltip title='Apportemission'>
            <LabelChip label='AE' onClick={onClick} sx={getIssueTypeColors(theme, 'Non-cash issue')} />
        </Tooltip>
    )
}

export const IPOLabel = ({
    onClick,
    noTooltip = false,
}: {
    onClick?: React.MouseEventHandler
    noTooltip?: boolean
}) => {
    const theme = useTheme()

    if (noTooltip) {
        return <LabelChip label='IPO' onClick={onClick} sx={getIssueTypeColors(theme, 'IPO')} />
    }

    return (
        <Tooltip title='IPO'>
            <LabelChip label='IPO' onClick={onClick} sx={getIssueTypeColors(theme, 'IPO')} />
        </Tooltip>
    )
}

export const ABBLabel = ({
    onClick,
    noTooltip = false,
}: {
    onClick?: React.MouseEventHandler
    noTooltip?: boolean
}) => {
    const theme = useTheme()

    if (noTooltip) {
        return <LabelChip label='ABB' onClick={onClick} sx={getIssueTypeColors(theme, 'ABB Directed issue')} />
    }

    return (
        <Tooltip title='Riktad emission (Accelererad bookbuilding)'>
            <LabelChip label='ABB' onClick={onClick} sx={getIssueTypeColors(theme, 'ABB Directed issue')} />
        </Tooltip>
    )
}

export const RELabel = ({ onClick, noTooltip = false }: { onClick?: React.MouseEventHandler; noTooltip?: boolean }) => {
    const theme = useTheme()

    if (noTooltip) {
        return <LabelChip label='RE' onClick={onClick} sx={getIssueTypeColors(theme, 'Directed issue')} />
    }

    return (
        <Tooltip title='Riktad emission'>
            <LabelChip label='RE' onClick={onClick} sx={getIssueTypeColors(theme, 'Directed issue')} />
        </Tooltip>
    )
}

export const GELabel = ({ noTooltip = false }: { noTooltip?: boolean }) => {
    if (noTooltip) {
        return <LabelChip label='GE' sx={{ backgroundColor: blue }} />
    }
    return (
        <Tooltip title='Garantiersättning'>
            <LabelChip label='GE' sx={{ backgroundColor: blue }} />
        </Tooltip>
    )
}

export const KVLabel = ({ noTooltip = false }: { noTooltip?: boolean }) => {
    const theme = useTheme()

    if (noTooltip) {
        return (
            <LabelChip
                label='KV'
                sx={{
                    backgroundColor: theme.palette.KV.main,
                    color: theme.palette.common.white,
                }}
            />
        )
    }

    return (
        <Tooltip title='Konvertibellån'>
            <LabelChip
                label='KV'
                sx={{
                    backgroundColor: theme.palette.KV.main,
                    color: theme.palette.common.white,
                }}
            />
        </Tooltip>
    )
}

// const UnitLabel = () => (
//     <Tooltip title='Unitemission'>
//         <LabelChip label='U' bg={amber} />
//     </Tooltip>
// )
// const ShareLabel = () => (
//     <Tooltip title='Aktieemission'>
//         <LabelChip label='A' bg={blue} />
//     </Tooltip>
// )

export const getIssueTypeColors = (theme: Theme, issueType: IssueType) => {
    switch (issueType) {
        case 'Rights issue':
            return {
                backgroundColor: theme.palette.FE.main,
                color: theme.palette.common.white,
            }
        case 'Non-cash issue':
            return {
                backgroundColor: theme.palette.AE.main,
                color: theme.palette.common.white,
            }
        case 'IPO':
            return {
                backgroundColor: theme.palette.IPO.main,
                color: theme.palette.common.white,
            }
        case 'ABB Directed issue':
            return {
                backgroundColor: theme.palette.ABB.main,
                color: theme.palette.common.white,
            }
        case 'Directed issue':
            return {
                backgroundColor: theme.palette.RE.main,
                color: theme.palette.common.white,
            }
    }
}

function IssueTypeLabels({
    issue,
    isClickable,
    isSingleLabel,
    noTooltip = false,
}:
    | {
          issue: Pick<IssueEntity, 'id' | 'hasWarrants' | 'type' | 'sharesInUnit' | 'unitExtraContent'>
          isClickable?: boolean
          isSingleLabel?: boolean
          noTooltip?: boolean
      }
    | {
          issue: Pick<IssueEntity, 'id' | 'type'>
          isClickable?: boolean
          isSingleLabel: true
          noTooltip?: boolean
      }) {
    const labels = []
    const handleClickIssue = () => {
        const path = `/issues/${issue.id}`

        window.open(path, '_blank')
    }

    switch (issue.type) {
        case 'Rights issue':
            labels.push(<FELabel key='FE' onClick={isClickable ? handleClickIssue : undefined} noTooltip={noTooltip} />)
            break
        case 'Non-cash issue':
            labels.push(
                <NonCashIssueLabel
                    key='nonCash'
                    onClick={isClickable ? handleClickIssue : undefined}
                    noTooltip={noTooltip}
                />,
            )
            break
        case 'IPO':
            labels.push(
                <IPOLabel key='IPO' onClick={isClickable ? handleClickIssue : undefined} noTooltip={noTooltip} />,
            )
            break
        case 'ABB Directed issue':
            labels.push(
                <ABBLabel key='ABB' onClick={isClickable ? handleClickIssue : undefined} noTooltip={noTooltip} />,
            )
            break
        case 'Directed issue':
            labels.push(<RELabel key='RE' onClick={isClickable ? handleClickIssue : undefined} noTooltip={noTooltip} />)
            break
    }

    if (!isSingleLabel) {
        if (isSpecialUnitIssue(issue)) {
            labels.push(<SPECLabel key='SPEC' noTooltip={noTooltip} />)
        }

        if (issue.hasWarrants && !isSpecialUnitIssue(issue)) {
            labels.push(<HasWarrantLabel key='hasWarrants' noTooltip={noTooltip} />)
        }
    }

    return (
        <Stack direction='row' alignItems='center' spacing={1} sx={{ display: 'inline-flex' }}>
            {labels}
        </Stack>
    )
}

export default IssueTypeLabels
